// xadomTableSlice.tsx
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Column, EditingCell, Filter, Row, SelectedColumn, SummaryColumn, TableState, ValueType } from '../types';

import { ROW_HEIGHT, SCROLLBAR_WIDTH } from './constants';
import { calculateHeaderHeight } from './functions';

// TablesState 관리: 테이블 ID를 키로 사용하여 각 테이블의 메타데이터를 저장
export interface TablesState {
    tables: {
        [tableId: string]: TableState;
    };
    checkedRows: Row[];
    lastCheckedCell?: {
        tableId: string;
        rowIndex: number;
        columnId: string;
    };
}

// 초기 상태
const initialState: TablesState = {
    tables: {},
    checkedRows: [],
    lastCheckedCell: undefined
};

// Slice 생성
const tablesSlice = createSlice({
    name: 'tables',
    initialState,
    reducers: {
        initializeTable: (
            state,
            action: PayloadAction<{
                tableId: string;
                columns: Column[];
                summaryColumns: SummaryColumn[];
                drawEmptyRows?: boolean;
                fullRowSelect?: boolean;
                rowFilter?: { [key: string]: (row: Row) => Row };
                autoFit?: boolean;
            }>
        ) => {
            const { tableId, columns, summaryColumns, drawEmptyRows, fullRowSelect, rowFilter, autoFit } = action.payload;

            // 1. 컬럼 구조 검증 및 처리
            const processedColumns = columns.reduce((acc, col) => {
                const processedColumn = validateColumnChildren(col, undefined, getMaxDepth(columns));
                processedColumn.visible = col.visible ?? true;
                acc[col.id] = processedColumn;
                return acc;
            }, {} as { [columnId: string]: Column });

            // 2. 모든 컬럼의 ratio 계산 (초기 로드 시)
            calculateAllColumnRatios(processedColumns);

            state.tables[tableId] = {
                tableId,
                columns: processedColumns,
                summaryColumns: summaryColumns.reduce((acc, col) => {
                    const newCol = { ...col, colspan: col.colspan || 1 };
                    acc[col.id] = newCol;
                    return acc;
                }, {} as { [summarycolumnId: string]: SummaryColumn }),
                hasSummaryColumn: summaryColumns.length > 0,
                selectedCell: { rowIndex: -1, columnIndex: -1 },
                selectedColumn: { id: '', grabbed: false },
                scrollX: 0,
                scrollY: 0,
                maxScrollX: 0,
                maxScrollY: 0,
                canvasWidth: 0,
                canvasHeight: 0,
                contentWidth: 0,
                contentHeight: 0,
                hasVerticalScrollbar: false,
                hasHorizontalScrollbar: false,
                editingCell: null,
                isEditing: false,
                autoAddRow: false,
                isInitialized: true,
                drawEmptyRows: drawEmptyRows,
                fullRowSelect: fullRowSelect ?? true,
                rowFilter: rowFilter ?? {},
                dropdownMenu: null,
                hoveredCell: null,
                isSearchModalOpen: false,
                autoFit: autoFit ?? false,
                autoFitPending: false,
            };
        },
        setColumns: (
            state,
            action: PayloadAction<{ tableId: string; columns: Column[] }>
        ) => {
            const { tableId, columns } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                // 컬럼 구조 검증 및 처리
                const processedColumns = columns.reduce((acc, col) => {
                    const processedColumn = validateColumnChildren(col, undefined, getMaxDepth(columns));
                    processedColumn.visible = col.visible ?? true;
                    acc[col.id] = processedColumn;
                    return acc;
                }, {} as { [columnId: string]: Column });

                table.columns = processedColumns;

                // autoFit이 활성화되어 있으면 width 기반으로 ratio 계산 후 캔버스에 맞게 적용
                console.log(table.canvasWidth);
                console.log(table.contentWidth);
                if (table.autoFit && table.canvasWidth > 0) {
                    // 컬럼들의 원래 width를 기준으로 ratio 계산
                    calculateAllColumnRatios(processedColumns);
                    // 계산된 ratio를 기준으로 캔버스 너비에 맞게 재분배
                    applyAutoFit(table);

                    // 초기 width 로그 reduce 로 출력
                    console.log('초기 width', Object.values(table.columns).reduce((acc, col) => {
                        acc[col.id] = col.width;
                        return acc;
                    }, {} as { [columnId: string]: number }));

                    // autoFit 후 width 로그 reduce 로 출력
                    console.log('autoFit 후 width', Object.values(table.columns).reduce((acc, col) => {
                        acc[col.id] = col.width;
                        return acc;
                    }, {} as { [columnId: string]: number }));
                } else if (table.autoFit) {
                    // 캔버스 너비가 없으면 일단 ratio만 계산하고 대기
                    calculateAllColumnRatios(processedColumns);
                    table.autoFitPending = true;
                } else {
                    // autoFit이 꺼져있으면 기본 ratio 계산
                    calculateAllColumnRatios(processedColumns);
                }
            }
        },
        setColumn: (
            state,
            action: PayloadAction<{ tableId: string; column: Column }>
        ) => {
            const { tableId, column } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.columns[column.id] = column;

                // autoFit이 활성화되어 있으면 적용
                if (table.autoFit && table.canvasWidth > 0) {
                    applyAutoFit(table);
                } else if (table.autoFit) {
                    table.autoFitPending = true;
                }
            }
        },
        setColumnWidth: (
            state,
            action: PayloadAction<{ tableId: string; columnId: string; width: number; sourceClass: string }>
        ) => {
            const { tableId, columnId, width } = action.payload;
            const table = state.tables[tableId];
            if (!table) return;

            const targetColumn = findColumnById(table, columnId);
            if (!targetColumn) return;

            if (table.autoFit) {
                // 자식 컬럼인지 확인
                if (targetColumn.parent) {
                    adjustChildColumnWidthInAutoFit(table, columnId, width);
                } else {
                    adjustColumnWidthInAutoFit(table, columnId, width);
                }
            } else {
                const minWidth = calculateMinWidth(targetColumn);
                const adjustedWidth = Math.max(width, minWidth);
                applyColumnWidth(targetColumn, adjustedWidth);
                updateParentColumnsWidth(table, columnId);
            }

            calculateScrollValues(table, true);
        },
        addColumn: (
            state,
            action: PayloadAction<{ tableId: string; column: Column }>
        ) => {
            const { tableId, column } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.columns[column.id] = column;

                // autoFit이 활성화되어 있으면 적용
                if (table.autoFit && table.canvasWidth > 0) {
                    applyAutoFit(table);
                } else if (table.autoFit) {
                    table.autoFitPending = true;
                }
            }
        },
        removeColumn: (
            state,
            action: PayloadAction<{ tableId: string; columnIndex: number }>
        ) => {
            const { tableId, columnIndex } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                delete table.columns[columnIndex];

                // autoFit이 활성화되어 있으면 적용
                if (table.autoFit && table.canvasWidth > 0) {
                    applyAutoFit(table);
                } else if (table.autoFit) {
                    table.autoFitPending = true;
                }
            }
        },
        setSummaryColumns: (
            state,
            action: PayloadAction<{ tableId: string; summaryColumns: SummaryColumn[] }>
        ) => {
            const { tableId, summaryColumns } = action.payload;
            if (state.tables[tableId]) {
                state.tables[tableId].summaryColumns = summaryColumns.reduce((acc, col) => {
                    acc[col.id] = col;
                    return acc;
                }, {} as { [summarycolumnId: string]: SummaryColumn });
                state.tables[tableId].hasSummaryColumn = summaryColumns.length > 0;
            }
        },
        addSummaryColumn: (
            state,
            action: PayloadAction<{ tableId: string; summaryColumn: SummaryColumn }>
        ) => {
            const { tableId, summaryColumn } = action.payload;
            if (state.tables[tableId]) {
                state.tables[tableId].summaryColumns[summaryColumn.id] = summaryColumn;
                state.tables[tableId].hasSummaryColumn = true;
            }
        },
        removeSummaryColumn: (
            state,
            action: PayloadAction<{ tableId: string; summarycolumnIndex: number }>
        ) => {
            const { tableId, summarycolumnIndex } = action.payload;
            if (state.tables[tableId]) {
                delete state.tables[tableId].summaryColumns[summarycolumnIndex];
                state.tables[tableId].hasSummaryColumn = Object.keys(state.tables[tableId].summaryColumns).length > 0;
            }
        },
        clearSummaryColumns: (
            state,
            action: PayloadAction<{ tableId: string }>
        ) => {
            const { tableId } = action.payload;
            if (state.tables[tableId]) {
                state.tables[tableId].summaryColumns = {};
                state.tables[tableId].hasSummaryColumn = false;
            }
        },
        clearSelectedCell: (
            state,
            action: PayloadAction<{ tableId: string }>
        ) => {
            const { tableId } = action.payload;
            if (state.tables[tableId]) {
                state.tables[tableId].selectedCell = { rowIndex: -1, columnIndex: -1 };
            }
        },
        setRowFilter: (
            state,
            action: PayloadAction<{ tableId: string; filter: { [key: string]: (row: Row) => Row } }>
        ) => {
            const { tableId, filter } = action.payload;
            if (state.tables[tableId]) {
                state.tables[tableId].rowFilter = filter;
            }
        },
        removeRowFilter: (
            state,
            action: PayloadAction<{ tableId: string }>
        ) => {
            const { tableId } = action.payload;
            if (state.tables[tableId]) {
                delete state.tables[tableId].rowFilter;
            }
        },
        setSelectedCell: (
            state,
            action: PayloadAction<{ tableId: string; rowIndex: number; columnIndex: number }>
        ) => {
            const { tableId, rowIndex, columnIndex } = action.payload;
            if (state.tables[tableId]) {
                state.tables[tableId].selectedCell = { rowIndex, columnIndex };
            }
        },
        setSelectedColumn: (
            state,
            action: PayloadAction<{ tableId: string; selectedColumn: SelectedColumn }>
        ) => {
            const { tableId, selectedColumn } = action.payload;
            if (state.tables[tableId]) {
                state.tables[tableId].selectedColumn = selectedColumn;
            }
        },
        setHasVerticalScrollbar: (
            state,
            action: PayloadAction<{ tableId: string; hasVerticalScrollbar: boolean }>
        ) => {
            const { tableId, hasVerticalScrollbar } = action.payload;
            if (state.tables[tableId]) {
                state.tables[tableId].hasVerticalScrollbar = hasVerticalScrollbar;
            }
        },
        setHasHorizontalScrollbar: (
            state,
            action: PayloadAction<{ tableId: string; hasHorizontalScrollbar: boolean }>
        ) => {
            const { tableId, hasHorizontalScrollbar } = action.payload;
            if (state.tables[tableId]) {
                state.tables[tableId].hasHorizontalScrollbar = hasHorizontalScrollbar;
            }
        },
        setScrollX: (
            state,
            action: PayloadAction<{ tableId: string; scrollX: number }>
        ) => {
            const { tableId, scrollX } = action.payload;
            if (state.tables[tableId]) {
                state.tables[tableId].scrollX = scrollX;
            }
        },
        setScrollY: (
            state,
            action: PayloadAction<{ tableId: string; scrollY: number }>
        ) => {
            const { tableId, scrollY } = action.payload;
            if (state.tables[tableId]) {
                state.tables[tableId].scrollY = scrollY;
            }
        },
        setScroll: (
            state,
            action: PayloadAction<{ tableId: string; scrollX: number; scrollY: number }>
        ) => {
            const { tableId, scrollX, scrollY } = action.payload;
            if (state.tables[tableId]) {
                state.tables[tableId].scrollX = scrollX;
                state.tables[tableId].scrollY = scrollY;
            }
        },
        setMaxScrollX: (
            state,
            action: PayloadAction<{ tableId: string; maxScrollX: number }>
        ) => {
            const { tableId, maxScrollX } = action.payload;
            if (state.tables[tableId]) {
                state.tables[tableId].maxScrollX = maxScrollX;
            }
        },
        setMaxScrollY: (
            state,
            action: PayloadAction<{ tableId: string; maxScrollY: number }>
        ) => {
            const { tableId, maxScrollY } = action.payload;
            if (state.tables[tableId]) {
                state.tables[tableId].maxScrollY = maxScrollY;
            }
        },
        setFilter: (
            state,
            action: PayloadAction<{ tableId: string; columnId: string; filter: Filter }>
        ) => {
            const { tableId, columnId, filter } = action.payload;
            if (state.tables[tableId]) {
                const column = findColumnById(state.tables[tableId], columnId);
                if (column) {
                    column.filter = filter;
                }
            }
        },
        setFilters: (
            state,
            action: PayloadAction<{ tableId: string; filters: { [columnId: string]: Filter } }>
        ) => {
            const { tableId, filters } = action.payload;
            if (state.tables[tableId]) {
                Object.entries(filters).forEach(([columnId, filter]) => {
                    state.tables[tableId].columns[columnId].filter = filter;
                });
            }
        },
        setEditingCell: (
            state,
            action: PayloadAction<{ tableId: string; editingCell: EditingCell | null }>
        ) => {
            const { tableId, editingCell } = action.payload;
            if (state.tables[tableId]) {
                state.tables[tableId].editingCell = editingCell;
            }
        },
        setCanvasSize: (
            state,
            action: PayloadAction<{ tableId: string; canvasWidth: number; canvasHeight: number }>
        ) => {
            const { tableId, canvasWidth, canvasHeight } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.canvasWidth = canvasWidth;
                table.canvasHeight = canvasHeight;
                // 스크롤 값 재계산 (캔버스 크기 변경 시 별도로 autoFit 적용)
                calculateScrollValues(table, true);
            }
        },
        setCanvasWidth: (
            state,
            action: PayloadAction<{ tableId: string; canvasWidth: number }>
        ) => {
            const { tableId, canvasWidth } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.canvasWidth = canvasWidth;
                // 스크롤 값 재계산
                calculateScrollValues(table);
            }
        },
        setCanvasHeight: (
            state,
            action: PayloadAction<{ tableId: string; canvasHeight: number }>
        ) => {
            const { tableId, canvasHeight } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.canvasHeight = canvasHeight;
                // 스크롤 값 재계산
                calculateScrollValues(table);
            }
        },
        setContentSize: (
            state,
            action: PayloadAction<{ tableId: string; contentWidth: number; contentHeight: number }>
        ) => {
            const { tableId, contentWidth, contentHeight } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.contentWidth = contentWidth;
                table.contentHeight = contentHeight;
                // 스크롤 값 재계산 (콘텐츠 크기는 이미 결정된 상태)
                calculateScrollValues(table, true);
            }
        },
        setContentWidth: (
            state,
            action: PayloadAction<{ tableId: string; contentWidth: number }>
        ) => {
            const { tableId, contentWidth } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.contentWidth = contentWidth;
                // 스크롤 값 재계산 (콘텐츠 너비는 이미 결정된 상태)
                calculateScrollValues(table, true);
            }
        },
        setContentHeight: (
            state,
            action: PayloadAction<{ tableId: string; contentHeight: number }>
        ) => {
            const { tableId, contentHeight } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.contentHeight = contentHeight;
                // 스크롤 값 재계산
                calculateScrollValues(table);
            }
        },
        setAutoFit: (
            state,
            action: PayloadAction<{ tableId: string; autoFit: boolean }>
        ) => {
            const { tableId, autoFit } = action.payload;
            const table = state.tables[tableId];
            if (table) {
                table.autoFit = autoFit;

                if (autoFit) {
                    // autoFit을 활성화할 때 현재 너비를 기준으로 비율 재계산
                    updateTopLevelColumnRatios(table);
                    // 그리고 autoFit 적용
                    applyAutoFit(table);
                }

                // autoFit 상태 변경 시 스크롤 값 재계산 (가로 스크롤 리셋)
                calculateScrollValues(table, true);
            }
        },
        applyAutoFitOnly: (
            state,
            action: PayloadAction<{ tableId: string }>
        ) => {
            const { tableId } = action.payload;
            const table = state.tables[tableId];
            if (table && table.autoFit) {
                // 비율 재계산 없이 기존 비율로 autoFit만 적용
                applyAutoFit(table);
                // 스크롤 값 재계산 (핫리로드 시에도 스크롤바가 정상적으로 계산되도록)
                calculateScrollValues(table, true);
            }
        },
        applyPendingAutoFit: (
            state,
            action: PayloadAction<{ tableId: string }>
        ) => {
            const { tableId } = action.payload;
            const table = state.tables[tableId];
            if (table && table.autoFit && table.autoFitPending) {
                applyAutoFit(table);
                table.autoFitPending = false;
                // 스크롤 값 재계산
                calculateScrollValues(table, true);
            }
        },
        clearAllOrderType: (
            state,
            action: PayloadAction<{ tableId: string }>
        ) => {
            const { tableId } = action.payload;
            if (state.tables[tableId]) {
                const nonGroupColumns = findNonGroupColumns(state.tables[tableId]);
                nonGroupColumns.forEach(col => {
                    delete col.filter?.orderType;
                });
            }
        },
        setDropdownMenu: (
            state,
            action: PayloadAction<{
                tableId: string;
                dropdownMenu: TableState['dropdownMenu'];
            }>
        ) => {
            const { tableId, dropdownMenu } = action.payload;
            state.tables[tableId].dropdownMenu = dropdownMenu;
        },
        addCheckedRowData: (state, action) => {
            state.checkedRows.push(action.payload);
        },
        removeCheckedRowData: (state, action) => {
            state.checkedRows = state.checkedRows.filter(row => row.id !== action.payload);
        },
        setAccountOptions: (
            state,
            action: PayloadAction<{ tableId: string; columnId: string; options: { value: number; label: string }[] }>
        ) => {
            const { tableId, columnId, options } = action.payload;
            if (state.tables[tableId]) {
                const column = findColumnById(state.tables[tableId], columnId);
                if (column) {
                    column.options = options;
                }
            }
        },
        setLastCheckedCell: (
            state,
            action: PayloadAction<{
                tableId: string;
                rowIndex: number;
                columnId: string;
            } | undefined>
        ) => {
            state.lastCheckedCell = action.payload;
        },
        setHoveredCell: (
            state,
            action: PayloadAction<{ tableId: string; hoveredCell: { rowIndex: number; columnIndex: number } | null }>
        ) => {
            const { tableId, hoveredCell } = action.payload;
            if (state.tables[tableId]) {
                state.tables[tableId].hoveredCell = hoveredCell;
            }
        },
        openSearchModal: (state, action) => {
            const { tableId } = action.payload;
            if (state.tables[tableId]) {
                state.tables[tableId].isSearchModalOpen = true;
            }
        },
        closeSearchModal: (state, action) => {
            const { tableId } = action.payload;
            if (state.tables[tableId]) {
                state.tables[tableId].isSearchModalOpen = false;
            }
        },
    }
});

export const {
    initializeTable,
    setColumns,
    setColumn,
    addColumn,
    removeColumn,
    setSummaryColumns,
    addSummaryColumn,
    removeSummaryColumn,
    clearSummaryColumns,
    setSelectedCell,
    clearSelectedCell,
    setSelectedColumn,
    setHasVerticalScrollbar,
    setHasHorizontalScrollbar,
    setScrollX,
    setScrollY,
    setScroll,
    setMaxScrollX,
    setMaxScrollY,
    setFilter,
    setFilters,
    setRowFilter,
    removeRowFilter,
    setEditingCell,
    setCanvasSize,
    setCanvasWidth,
    setCanvasHeight,
    setContentSize,
    setContentWidth,
    setContentHeight,
    setColumnWidth,
    setAutoFit,
    applyAutoFitOnly,
    applyPendingAutoFit,
    clearAllOrderType,
    setDropdownMenu,
    addCheckedRowData,
    removeCheckedRowData,
    setAccountOptions,
    setLastCheckedCell,
    setHoveredCell,
    openSearchModal,
    closeSearchModal,
} = tablesSlice.actions;

export default tablesSlice;

function calculateScrollValues(table: TableState, skipAutoFit = false) {
    const { canvasWidth, canvasHeight, contentWidth, contentHeight, hasSummaryColumn } = table;

    const headerHeight = calculateHeaderHeight(table.columns);
    const summaryHeight = hasSummaryColumn ? ROW_HEIGHT : 0;
    
    // 현재 세로 스크롤바 상태 저장 (변경 감지용)
    const previousHasVerticalScrollbar = table.hasVerticalScrollbar;
    
    // 세로 스크롤바 먼저 계산 (가로 스크롤바 없다고 가정)
    const tempVisibleHeight = canvasHeight - headerHeight - summaryHeight - 1;
    const hasVerticalScrollbar = contentHeight > tempVisibleHeight;
    
    // 가로 스크롤바 계산 (세로 스크롤바 공간 고려)
    const availableWidth = hasVerticalScrollbar ? canvasWidth - SCROLLBAR_WIDTH : canvasWidth;
    const hasHorizontalScrollbar = contentWidth > availableWidth;
    
    // 최종 사용 가능한 높이 계산 (가로 스크롤바 공간 고려)
    const finalVisibleHeight = canvasHeight - headerHeight - summaryHeight - (hasHorizontalScrollbar ? SCROLLBAR_WIDTH : 0) - 1;
    const diff = finalVisibleHeight % ROW_HEIGHT;
    
    const maxScrollX = Math.max(contentWidth - availableWidth, 0);
    const maxScrollY = Math.max(contentHeight - finalVisibleHeight + ROW_HEIGHT + diff, 0);

    table.maxScrollX = maxScrollX;
    table.maxScrollY = maxScrollY;
    table.hasVerticalScrollbar = hasVerticalScrollbar;
    table.hasHorizontalScrollbar = hasHorizontalScrollbar;

    // 스크롤 위치가 최대치를 넘지 않도록 조정
    table.scrollX = Math.min(table.scrollX, maxScrollX);
    table.scrollY = Math.min(table.scrollY, maxScrollY);
    
    // 세로 스크롤바 상태가 변경되었고 autoFit이 활성화되어 있으면 즉시 적용
    if (!skipAutoFit && table.autoFit && previousHasVerticalScrollbar !== hasVerticalScrollbar) {
        applyAutoFit(table);
        // autoFit 적용 후 스크롤 값 재계산 (무한 루프 방지를 위해 skipAutoFit=true)
        calculateScrollValues(table, true);
    }
}

// GROUP이 아닌 모든 컬럼들을 찾는 헬퍼 함수
function findNonGroupColumns(table: TableState): Column[] {
    const result: Column[] = [];

    function searchNonGroupColumns(columns: Column[]) {
        columns.forEach(column => {
            if (column.type !== ValueType.GROUP) {
                result.push(column);
            }

            // 자식 컬럼이 있다면 재귀적으로 검색
            if (column.children && column.children.length > 0) {
                searchNonGroupColumns(column.children);
            }
        });
    }

    searchNonGroupColumns(Object.values(table.columns));
    return result;
}

// 컬럼 ID로 컬럼을 찾는 헬퍼 함수
function findColumnById(table: TableState, targetId: string): Column | undefined {
    // 모든 최상위 컬럼들을 배열로 변환
    const columns = Object.values(table.columns);

    // 재귀적으로 컬럼을 검색하는 내부 함수
    function searchInColumns(columns: Column[]): Column | undefined {
        for (const column of columns) {
            // 현재 컬럼의 ID가 일치하는지 확인
            if (column.id === targetId) {
                return column;
            }

            // 자식 컬럼이 있다면 재귀적으로 검색
            if (column.children && column.children.length > 0) {
                const foundInChildren = searchInColumns(column.children);
                if (foundInChildren) {
                    return foundInChildren;
                }
            }
        }
        return undefined;
    }

    return searchInColumns(columns);
}

/**
 * 모든 컬럼의 ratio를 계산하는 함수 (초기 로드 시 사용)
 */
function calculateAllColumnRatios(columns: { [columnId: string]: Column }) {
    // 1. 최상위 컬럼들의 ratio 계산
    const topLevelColumns = Object.values(columns).filter(col => !col.parent && col.visible !== false);
    if (topLevelColumns.length > 0) {
        const totalWidth = topLevelColumns.reduce((sum, col) => sum + col.width, 0);
        topLevelColumns.forEach(col => {
            col.ratio = totalWidth > 0 ? col.width / totalWidth : 1 / topLevelColumns.length;
        });
    }

    // 2. GROUP 컬럼들의 자식 ratio 계산
    function calculateChildRatios(column: Column) {
        if (column.type === ValueType.GROUP && column.children?.length) {
            const totalChildWidth = column.children.reduce((sum, child) => sum + child.width, 0);
            column.children.forEach(child => {
                child.ratio = totalChildWidth > 0 ? child.width / totalChildWidth : 1 / column.children!.length;
                // 재귀적으로 자식의 자식들도 처리
                calculateChildRatios(child);
            });
        }
    }

    Object.values(columns).forEach(calculateChildRatios);
}

/**
 * 특정 컬럼의 너비를 적용하는 함수
 */
function applyColumnWidth(column: Column, newWidth: number) {
    const MIN_WIDTH = 30;
    column.width = Math.max(newWidth, MIN_WIDTH);

    // GROUP 컬럼인 경우 자식들도 비례적으로 조정
    if (column.type === ValueType.GROUP && column.children?.length) {
        const children = column.children;
        let remainingWidth = column.width;

        children.forEach((child, index) => {
            if (index === children.length - 1) {
                // 마지막 자식은 남은 너비 전체 할당
                child.width = Math.max(remainingWidth, MIN_WIDTH);
            } else {
                // ratio에 따른 너비 계산
                const calculatedWidth = Math.round(column.width * (child.ratio || 0));
                child.width = Math.max(calculatedWidth, MIN_WIDTH);
                remainingWidth -= child.width;
            }

            // 자식이 GROUP인 경우 재귀적으로 조정
            if (child.type === ValueType.GROUP && child.children?.length) {
                applyColumnWidth(child, child.width);
            }
        });
    }
}

/**
 * 부모 컬럼들의 너비만 업데이트하는 함수
 */
function updateParentColumnsWidth(table: TableState, columnId: string) {
    let currentColumn = findColumnById(table, columnId);
    if (!currentColumn) return;

    let parentColumn = currentColumn.parent ? findColumnById(table, currentColumn.parent) : null;
    while (parentColumn) {
        if (parentColumn.type === ValueType.GROUP && parentColumn.children) {
            // 자식들의 총 너비로 부모 너비 업데이트
            parentColumn.width = parentColumn.children.reduce((sum, child) => sum + child.width, 0);
        }
        parentColumn = parentColumn.parent ? findColumnById(table, parentColumn.parent) : null;
    }
}

/**
 * 특정 컬럼의 ratio만 업데이트하는 함수
 */
function updateSingleColumnRatio(table: TableState, columnId: string) {
    const targetColumn = findColumnById(table, columnId);
    if (!targetColumn) return;

    // 최상위 컬럼인 경우
    if (!targetColumn.parent) {
        const topLevelColumns = Object.values(table.columns)
            .filter(col => !col.parent && col.visible !== false);

        if (topLevelColumns.length === 0) return;

        const totalWidth = topLevelColumns.reduce((sum, col) => sum + col.width, 0);
        if (totalWidth > 0) {
            targetColumn.ratio = targetColumn.width / totalWidth;
        }
    }
    // GROUP 컬럼의 자식인 경우
    else {
        const parentColumn = findColumnById(table, targetColumn.parent);
        if (parentColumn && parentColumn.children) {
            const totalChildWidth = parentColumn.children.reduce((sum, child) => sum + child.width, 0);
            if (totalChildWidth > 0) {
                targetColumn.ratio = targetColumn.width / totalChildWidth;
            }
        }
    }
}

/**
 * AutoFit을 적용하는 함수 (ratio 기준으로 width 재계산)
 */
function applyAutoFit(table: TableState) {
    if (!table.autoFit || table.canvasWidth <= 0) return;

    // 최상위 컬럼들만 처리
    const topLevelColumns = Object.values(table.columns)
        .filter(col => !col.parent && col.visible !== false);

    if (topLevelColumns.length === 0) return;

    // 세로 스크롤바 공간만 고려 (가로 스크롤바는 자동 계산됨)
    const verticalScrollbarSpace = table.hasVerticalScrollbar ? SCROLLBAR_WIDTH : 0;
    const availableWidth = table.canvasWidth - verticalScrollbarSpace;
    const MIN_WIDTH = 30;

    // ratio에 따라 너비 재분배
    let accumulatedWidth = 0;
    topLevelColumns.forEach((col, index) => {
        if (index === topLevelColumns.length - 1) {
            // 마지막 컬럼은 남은 너비 전체 할당
            col.width = Math.max(availableWidth - accumulatedWidth, MIN_WIDTH);
        } else {
            // ratio에 따른 너비 계산
            const calculatedWidth = Math.round(availableWidth * (col.ratio || 0));
            col.width = Math.max(calculatedWidth, MIN_WIDTH);
            accumulatedWidth += col.width;
        }

        // GROUP 컬럼인 경우 자식들도 비례적으로 조정
        if (col.type === ValueType.GROUP && col.children?.length) {
            applyColumnWidth(col, col.width);
        }
    });

    // 전체 너비 업데이트
    const newTotalWidth = topLevelColumns.reduce((sum, col) => sum + col.width, 0);
    table.contentWidth = newTotalWidth;
}

/**
 * autoFit 모드에서 컬럼 너비를 조정하는 전용 함수
 * @param table 테이블 상태
 * @param columnId 조절할 컬럼의 ID
 * @param targetWidth 목표 너비
 */
function adjustColumnWidthInAutoFit(table: TableState, columnId: string, targetWidth: number) {
    if (!table.autoFit || table.canvasWidth <= 0) return;

    const topLevelColumns = Object.values(table.columns)
        .filter(col => !col.parent && col.visible !== false);

    if (topLevelColumns.length === 0) return;

    // 세로 스크롤바 공간만 고려 (가로 스크롤바는 자동 계산됨)
    const verticalScrollbarSpace = table.hasVerticalScrollbar ? SCROLLBAR_WIDTH : 0;
    const availableWidth = table.canvasWidth - verticalScrollbarSpace;
    const targetColumn = findColumnById(table, columnId);

    if (!targetColumn) return;

    const minWidth = calculateMinWidth(targetColumn);
    const otherColumns = topLevelColumns.filter(col => col.id !== columnId);

    const otherColumnsMinWidth = otherColumns.reduce((sum, col) => sum + calculateMinWidth(col), 0);
    const maxPossibleTargetWidth = availableWidth - otherColumnsMinWidth;

    let currentTargetWidth = Math.max(minWidth, Math.min(targetWidth, maxPossibleTargetWidth));

    if (otherColumns.length > 0) {
        let iteration = 0;
        const maxIterations = otherColumns.length + 2;
        let fixedColumns: Column[] = [];
        let flexibleColumns = [...otherColumns];

        while (iteration < maxIterations) {
            iteration++;

            const fixedWidth = fixedColumns.reduce((sum, col) => sum + col.width, 0);
            const availableForFlexible = availableWidth - fixedWidth;
            const flexibleMinWidth = flexibleColumns.reduce((sum, col) => sum + calculateMinWidth(col), 0);
            const actualMaxTargetWidth = availableForFlexible - flexibleMinWidth;
            const actualTargetWidth = Math.max(minWidth, Math.min(currentTargetWidth, actualMaxTargetWidth));

            if (actualTargetWidth !== currentTargetWidth) {
                currentTargetWidth = actualTargetWidth;
            }

            applyColumnWidth(targetColumn, currentTargetWidth);

            const remainingForFlexible = availableForFlexible - currentTargetWidth;

            if (flexibleColumns.length === 0) {
                break;
            }

            const flexibleRatioSum = flexibleColumns.reduce((sum, col) => sum + (col.ratio || 0), 0);

            if (flexibleRatioSum <= 0) {
                const equalWidth = Math.floor(remainingForFlexible / flexibleColumns.length);
                let accumulatedWidth = 0;

                flexibleColumns.forEach((col, index) => {
                    if (index === flexibleColumns.length - 1) {
                        col.width = Math.max(remainingForFlexible - accumulatedWidth, calculateMinWidth(col));
                    } else {
                        col.width = Math.max(equalWidth, calculateMinWidth(col));
                        accumulatedWidth += col.width;
                    }

                    if (col.type === ValueType.GROUP && col.children?.length) {
                        applyColumnWidth(col, col.width);
                    }
                });
                break;
            }

            const plannedWidths = flexibleColumns.map(col => {
                const normalizedRatio = (col.ratio || 0) / flexibleRatioSum;
                const plannedWidth = Math.round(remainingForFlexible * normalizedRatio);
                const minWidth = calculateMinWidth(col);
                return {
                    column: col,
                    plannedWidth,
                    minWidth,
                    needsFixing: plannedWidth < minWidth
                };
            });

            const columnsToFix = plannedWidths.filter(item => item.needsFixing);

            if (columnsToFix.length === 0) {
                let accumulatedWidth = 0;
                plannedWidths.forEach((item, index) => {
                    if (index === plannedWidths.length - 1) {
                        const remaining = remainingForFlexible - accumulatedWidth;
                        item.column.width = Math.max(remaining, item.minWidth);
                    } else {
                        item.column.width = item.plannedWidth;
                        accumulatedWidth += item.column.width;
                    }

                    if (item.column.type === ValueType.GROUP && item.column.children?.length) {
                        applyColumnWidth(item.column, item.column.width);
                    }
                });
                break;
            } else {
                columnsToFix.forEach(item => {
                    item.column.width = item.minWidth;
                    fixedColumns.push(item.column);

                    if (item.column.type === ValueType.GROUP && item.column.children?.length) {
                        applyColumnWidth(item.column, item.column.width);
                    }
                });

                flexibleColumns = flexibleColumns.filter(col =>
                    !columnsToFix.some(item => item.column.id === col.id)
                );
            }
        }
    } else {
        applyColumnWidth(targetColumn, Math.min(currentTargetWidth, availableWidth));
    }

    updateParentColumnsWidth(table, columnId);
    otherColumns.forEach(col => {
        if (col.parent) {
            updateParentColumnsWidth(table, col.id);
        }
    });

    const newTotalWidth = topLevelColumns.reduce((sum, col) => sum + col.width, 0);
    if (newTotalWidth > 0) {
        const newTargetRatio = targetColumn.width / newTotalWidth;
        targetColumn.ratio = newTargetRatio;

        const otherColumnsOriginalRatioSum = otherColumns.reduce((sum, col) => sum + (col.ratio || 0), 0);
        const remainingRatioSpace = 1 - newTargetRatio;

        if (otherColumnsOriginalRatioSum > 0 && remainingRatioSpace > 0) {
            otherColumns.forEach(col => {
                const originalRatio = col.ratio || 0;
                col.ratio = remainingRatioSpace * (originalRatio / otherColumnsOriginalRatioSum);
            });
        }
    }

    const finalTotalWidth = topLevelColumns.reduce((sum, col) => sum + col.width, 0);
    const excessWidth = finalTotalWidth - availableWidth;

    if (excessWidth > 0) {
        const currentTargetColumnWidth = targetColumn.width;
        const newTargetWidth = Math.max(currentTargetColumnWidth - excessWidth, calculateMinWidth(targetColumn));
        const actualReduction = currentTargetColumnWidth - newTargetWidth;

        applyColumnWidth(targetColumn, newTargetWidth);

        if (actualReduction < excessWidth) {
            const remainingExcess = excessWidth - actualReduction;
            const adjustableColumns = otherColumns.filter(col => col.width > calculateMinWidth(col));
            if (adjustableColumns.length > 0) {
                const reductionPerColumn = Math.ceil(remainingExcess / adjustableColumns.length);
                adjustableColumns.forEach(col => {
                    const currentWidth = col.width;
                    const minWidth = calculateMinWidth(col);
                    const maxReduction = currentWidth - minWidth;
                    const actualColumnReduction = Math.min(reductionPerColumn, maxReduction);

                    if (actualColumnReduction > 0) {
                        col.width = currentWidth - actualColumnReduction;

                        if (col.type === ValueType.GROUP && col.children?.length) {
                            applyColumnWidth(col, col.width);
                        }
                    }
                });
            }
        }
    }

    const correctedTotalWidth = topLevelColumns.reduce((sum, col) => sum + col.width, 0);
    table.contentWidth = correctedTotalWidth + SCROLLBAR_WIDTH;
}

function adjustChildColumnWidthInAutoFit(table: TableState, columnId: string, targetWidth: number) {
    if (!table.autoFit) return;

    const targetColumn = findColumnById(table, columnId);
    if (!targetColumn || !targetColumn.parent) return;

    const parentColumn = findColumnById(table, targetColumn.parent);
    if (!parentColumn || !parentColumn.children) return;

    const minWidth = calculateMinWidth(targetColumn);
    const adjustedTargetWidth = Math.max(targetWidth, minWidth);

    // 형제 컬럼들 찾기
    const siblingColumns = parentColumn.children.filter(col => col.id !== columnId);
    
    // 마지막 자식 컬럼인지 확인 (인덱스 기준)
    const targetIndex = parentColumn.children.findIndex(col => col.id === columnId);
    const isLastChild = targetIndex === parentColumn.children.length - 1;
    
    if (isLastChild) {
        // 마지막 자식 컬럼인 경우: 부모 컬럼도 함께 변경
        const currentParentWidth = parentColumn.width;
        const currentTargetWidth = targetColumn.width;
        const widthDifference = adjustedTargetWidth - currentTargetWidth;
        
        // 목표 컬럼 너비 설정
        targetColumn.width = adjustedTargetWidth;
        
        // 부모 컬럼 너비 조정
        const newParentWidth = currentParentWidth + widthDifference;
        parentColumn.width = Math.max(newParentWidth, calculateMinWidth(parentColumn));
        
        // 부모 컬럼의 ratio 업데이트 (형제들과의 새로운 비율)
        const newParentTotalWidth = parentColumn.children.reduce((sum, child) => sum + child.width, 0);
        if (newParentTotalWidth > 0) {
            parentColumn.children.forEach(child => {
                child.ratio = child.width / newParentTotalWidth;
            });
        }
        
        // 재귀적으로 상위 부모들 업데이트
        updateParentColumnsWidthRecursively(table, targetColumn.parent);
        
        // 최상위 레벨에 도달했으면 전체 autoFit 재적용
        const rootParent = findRootParent(table, targetColumn.parent);
        if (rootParent) {
            // 최상위 컬럼들의 비율 업데이트
            updateTopLevelColumnRatios(table);
            // autoFit 재적용
            applyAutoFit(table);
        }
    } else {
        // 마지막이 아닌 자식 컬럼인 경우: 기존 로직 (부모 너비 유지)
        const currentParentWidth = parentColumn.width;
        
        // 형제 컬럼들의 최소 너비 합계
        const siblingsMinWidth = siblingColumns.reduce((sum, col) => sum + calculateMinWidth(col), 0);
        
        // 목표 컬럼이 가질 수 있는 최대 너비
        const maxPossibleWidth = currentParentWidth - siblingsMinWidth;
        const finalTargetWidth = Math.min(adjustedTargetWidth, maxPossibleWidth);

        // 목표 컬럼 너비 설정
        targetColumn.width = finalTargetWidth;

        // 형제 컬럼들에게 남은 너비 분배
        const remainingWidth = currentParentWidth - finalTargetWidth;
        
        if (siblingColumns.length > 0 && remainingWidth > 0) {
            // 형제들의 원래 비율 합계
            const siblingsRatioSum = siblingColumns.reduce((sum, col) => sum + (col.ratio || 0), 0);
            
            if (siblingsRatioSum > 0) {
                // 비율에 따라 남은 너비 분배
                let accumulatedWidth = 0;
                siblingColumns.forEach((col, index) => {
                    if (index === siblingColumns.length - 1) {
                        // 마지막 형제는 남은 너비 전체 할당
                        col.width = Math.max(remainingWidth - accumulatedWidth, calculateMinWidth(col));
                    } else {
                        const targetWidth = Math.round(remainingWidth * (col.ratio || 0) / siblingsRatioSum);
                        col.width = Math.max(targetWidth, calculateMinWidth(col));
                        accumulatedWidth += col.width;
                    }

                    // GROUP 컬럼인 경우 자식들도 비례적으로 조정
                    if (col.type === ValueType.GROUP && col.children?.length) {
                        applyColumnWidth(col, col.width);
                    }
                });
            } else {
                // 비율이 없으면 균등 분배
                const equalWidth = Math.floor(remainingWidth / siblingColumns.length);
                let accumulatedWidth = 0;
                
                siblingColumns.forEach((col, index) => {
                    if (index === siblingColumns.length - 1) {
                        col.width = Math.max(remainingWidth - accumulatedWidth, calculateMinWidth(col));
                    } else {
                        col.width = Math.max(equalWidth, calculateMinWidth(col));
                        accumulatedWidth += col.width;
                    }

                    // GROUP 컬럼인 경우 자식들도 비례적으로 조정
                    if (col.type === ValueType.GROUP && col.children?.length) {
                        applyColumnWidth(col, col.width);
                    }
                });
            }
        }

        // 목표 컬럼이 GROUP인 경우 자식들도 비례적으로 조정
        if (targetColumn.type === ValueType.GROUP && targetColumn.children?.length) {
            applyColumnWidth(targetColumn, targetColumn.width);
        }

        // 부모 컬럼의 ratio 업데이트 (형제들과의 새로운 비율)
        const newParentTotalWidth = parentColumn.children.reduce((sum, child) => sum + child.width, 0);
        if (newParentTotalWidth > 0) {
            parentColumn.children.forEach(child => {
                child.ratio = child.width / newParentTotalWidth;
            });
        }
    }
}

function calculateMinWidth(column: Column): number {
    const MIN_COLUMN_WIDTH = 30;

    if (column.type !== ValueType.GROUP || !column.children || column.children.length === 0) {
        return MIN_COLUMN_WIDTH;
    }

    // GROUP 컬럼의 경우 모든 자식 컬럼의 최소 너비 합계를 반환
    return column.children.reduce((sum, child) => sum + calculateMinWidth(child), 0);
}

function calculateColumnWidthConstraints(column: Column, widthMap: Map<string, { width: number; minWidth: number }>) {
    const minWidth = calculateMinWidth(column);
    widthMap.set(column.id, { width: column.width, minWidth });

    if (column.type === ValueType.GROUP && column.children?.length) {
        column.children.forEach(child => calculateColumnWidthConstraints(child, widthMap));
    }
}

function validateColumnChildren(
    column: Column,
    parentId?: string,
    totalDepth?: number,
    currentDepth: number = 1
): Column {
    // 현재 컬럼의 복사본 생성
    const processedColumn: Column = { ...column };

    // GROUP 타입인데 children이 없거나 비어있는 경우 오류 발생
    if (processedColumn.type === ValueType.GROUP && (!processedColumn.children || processedColumn.children.length === 0)) {
        throw new Error(
            `GROUP type column must have children (column id: ${processedColumn.id})`
        );
    }

    // parent 설정 및 ratio 검증
    if (parentId) {
        processedColumn.parent = parentId;
        if (processedColumn.ratio === undefined) {
            throw new Error(
                `Column with parent must have a ratio value (column id: ${processedColumn.id})`
            );
        }
    }

    // children이 있는 경우 재귀적으로 처리
    if (processedColumn.children) {
        processedColumn.children = processedColumn.children.map(child =>
            validateColumnChildren(child, processedColumn.id, totalDepth, currentDepth + 1)
        );
    }

    // 컬럼 타입 검증
    if (processedColumn.type !== ValueType.GROUP && processedColumn.children && processedColumn.children.length > 0) {
        throw new Error(
            `Non-GROUP column type '${processedColumn.type}' cannot have children (column id: ${processedColumn.id})`
        );
    }

    return processedColumn;
}

// 전체 컬럼 트리의 최대 깊이 계산
function getMaxDepth(columns: Column[]): number {
    let maxDepth = 1;
    for (const column of columns) {
        if (column.children && column.children.length > 0) {
            const childDepth = getMaxDepth(column.children) + 1;
            maxDepth = Math.max(maxDepth, childDepth);
        }
    }
    return maxDepth;
}

/**
 * 최상위 컬럼들의 비율을 현재 너비를 기준으로 재계산하는 함수
 * @param table 테이블 상태
 */
function updateTopLevelColumnRatios(table: TableState) {
    const topLevelColumns = Object.values(table.columns)
        .filter(col => !col.parent && col.visible !== false);

    if (topLevelColumns.length === 0) return;

    const totalWidth = topLevelColumns.reduce((sum, col) => sum + col.width, 0);
    if (totalWidth > 0) {
        topLevelColumns.forEach(col => {
            col.ratio = col.width / totalWidth;
        });
    }
}

/**
 * autoFit 모드에서 특정 컬럼의 너비가 변경되었을 때, 다른 컬럼들의 너비를 조정하는 함수
 * @param table 테이블 상태
 * @param changedColumnId 변경된 컬럼의 ID
 */
function adjustOtherColumnsWidth(table: TableState, changedColumnId: string) {
    if (!table.autoFit || table.canvasWidth <= 0) return;

    const topLevelColumns = Object.values(table.columns)
        .filter(col => !col.parent && col.visible !== false);

    if (topLevelColumns.length <= 1) return;

    const availableWidth = table.canvasWidth - SCROLLBAR_WIDTH;
    const changedColumn = topLevelColumns.find(col => col.id === changedColumnId);

    if (!changedColumn) return;

    // 변경된 컬럼을 제외한 나머지 컬럼들
    const otherColumns = topLevelColumns.filter(col => col.id !== changedColumnId);

    const MIN_WIDTH = 30;

    // 다른 컬럼들의 최소 너비 합계 계산
    const otherColumnsMinWidth = otherColumns.reduce((sum, col) => sum + calculateMinWidth(col), 0);

    // 조절된 컬럼의 최대 허용 너비 계산
    const maxAllowedWidthForChanged = availableWidth - otherColumnsMinWidth;

    // 조절된 컬럼의 너비가 최대 허용 너비를 초과하면 제한
    if (changedColumn.width > maxAllowedWidthForChanged) {
        changedColumn.width = Math.max(maxAllowedWidthForChanged, calculateMinWidth(changedColumn));
        // GROUP 컬럼인 경우 자식들도 비례적으로 조정
        if (changedColumn.type === ValueType.GROUP && changedColumn.children?.length) {
            applyColumnWidth(changedColumn, changedColumn.width);
        }
    }

    // 나머지 컬럼들이 사용할 수 있는 너비 계산
    const remainingWidth = availableWidth - changedColumn.width;

    // 다른 컬럼들의 원래 ratio 합계 계산
    const otherColumnsRatioSum = otherColumns.reduce((sum, col) => sum + (col.ratio || 0), 0);

    if (otherColumnsRatioSum > 0) {
        // 각 컬럼의 원래 ratio에 따라 남은 너비를 분배
        let accumulatedWidth = 0;
        otherColumns.forEach((col, index) => {
            if (index === otherColumns.length - 1) {
                // 마지막 컬럼은 남은 너비 전체 할당
                col.width = Math.max(remainingWidth - accumulatedWidth, calculateMinWidth(col));
            } else {
                // 원래 ratio에 따라 너비 계산
                const targetWidth = Math.round(remainingWidth * (col.ratio || 0) / otherColumnsRatioSum);
                col.width = Math.max(targetWidth, calculateMinWidth(col));
                accumulatedWidth += col.width;
            }

            // GROUP 컬럼인 경우 자식들도 비례적으로 조정
            if (col.type === ValueType.GROUP && col.children?.length) {
                applyColumnWidth(col, col.width);
            }
        });
    } else {
        // ratio가 없는 경우 균등 분배
        const equalWidth = Math.floor(remainingWidth / otherColumns.length);
        let accumulatedWidth = 0;

        otherColumns.forEach((col, index) => {
            if (index === otherColumns.length - 1) {
                // 마지막 컬럼은 남은 너비 전체 할당
                col.width = Math.max(remainingWidth - accumulatedWidth, calculateMinWidth(col));
            } else {
                col.width = Math.max(equalWidth, calculateMinWidth(col));
                accumulatedWidth += col.width;
            }

            // GROUP 컬럼인 경우 자식들도 비례적으로 조정
            if (col.type === ValueType.GROUP && col.children?.length) {
                applyColumnWidth(col, col.width);
            }
        });
    }

    // 전체 너비 업데이트
    const newTotalWidth = topLevelColumns.reduce((sum, col) => sum + col.width, 0);
    table.contentWidth = newTotalWidth + SCROLLBAR_WIDTH;
}

function updateParentColumnsWidthRecursively(table: TableState, parentId: string) {
    const parentColumn = findColumnById(table, parentId);
    if (!parentColumn) return;

    // 현재 부모의 너비를 자식들의 합계로 업데이트
    if (parentColumn.type === ValueType.GROUP && parentColumn.children) {
        parentColumn.width = parentColumn.children.reduce((sum, child) => sum + child.width, 0);
    }

    // 더 상위 부모가 있으면 재귀적으로 계속 업데이트
    if (parentColumn.parent) {
        updateParentColumnsWidthRecursively(table, parentColumn.parent);
    }
}

function findRootParent(table: TableState, columnId: string): Column | null {
    const column = findColumnById(table, columnId);
    if (!column) return null;

    // 부모가 없으면 자신이 최상위
    if (!column.parent) {
        return column;
    }

    // 부모가 있으면 재귀적으로 최상위 찾기
    return findRootParent(table, column.parent);
}